/**
 * Single source of truth for page metadata.
 *
 * This file is imported by BOTH:
 *   - the React app (src/seo/useSeo.js) — updates tags during client-side navigation
 *   - the Netlify edge function (netlify/edge-functions/seo.js) — injects tags into the
 *     HTML *before* it reaches the browser, which is the only thing social crawlers read.
 *
 * Because the Deno-based edge function imports it directly, keep this file:
 *   - plain ESM with NO imports of its own
 *   - free of any browser or Node built-ins
 */

export const SITE_NAME = "Legacy Timepieces";
export const SITE_TAGLINE = "Luxury Watch Specialist";
export const TITLE_SUFFIX = ` | ${SITE_NAME}`;
export const LOCALE = "en_ID";
export const TWITTER_HANDLE = "";

/** Default share image — the branded boutique banner, NOT the favicon. 1200x630. */
export const DEFAULT_OG_IMAGE = "/og-banner.jpg";
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;
export const DEFAULT_OG_IMAGE_ALT = `${SITE_NAME} — ${SITE_TAGLINE}`;

export const ORGANIZATION = {
  legalName: "Legacy Timepieces",
  foundingDate: "2015",
  phone: "+62 851-7822-8989",
  whatsapp: "6285178228989",
  street: "Plaza Blok M, 2nd Floor, Unit 2.24-2.25",
  locality: "Jakarta Selatan",
  region: "DKI Jakarta",
  country: "ID",
  instagram: "https://instagram.com",
};

/**
 * Static route metadata. Keys are matched exactly against the pathname.
 * `/watches/:id` is handled dynamically (see buildProductMeta).
 */
export const ROUTE_META = {
  "/": {
    title: `${SITE_NAME} — Luxury & Pre-Owned Watches in Jakarta`,
    description:
      "Authenticated luxury & pre-owned watches — Rolex, Patek Philippe, Audemars Piguet and more. Trusted by collectors since 2015. Plaza Blok M, Jakarta.",
  },
  "/watches": {
    title: "All Watches — Browse the Collection",
    description:
      "Browse thousands of authenticated luxury and pre-owned timepieces. Filter by brand, model, movement, case, dial and more to find your next watch.",
  },
  "/brands": {
    title: "All Brands — Luxury Watch Maisons We Carry",
    description:
      "Explore every luxury watch brand available at Legacy Timepieces, from Rolex and Omega to Patek Philippe and Richard Mille. Browse each maison's collection.",
  },
  "/services": {
    title: "Our Services — Buy, Sell & Trade Luxury Watches",
    description:
      "Buy, sell, trade or consign your luxury watch with Legacy Timepieces. Free valuation, authenticated timepieces and three generations of family expertise.",
  },
  "/about": {
    title: "About Us — A Decade of Trusted Timekeeping",
    description:
      "Since 2015, Legacy Timepieces has built its reputation on authenticity, curation and trust. Read our story, values and milestones.",
  },
};

export const NOT_FOUND_META = {
  title: "Page Not Found",
  description: "The page you are looking for does not exist.",
  noindex: true,
};

/** Build a page title, avoiding "Legacy Timepieces | Legacy Timepieces". */
export const formatTitle = (title) => {
  if (!title) return `${SITE_NAME} — ${SITE_TAGLINE}`;
  return title.includes(SITE_NAME) ? title : `${title}${TITLE_SUFFIX}`;
};

export const truncate = (text, max = 160) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/[\s,;:.-]+$/, "")}…`;
};

/** Absolute URL for any path, given the site origin. */
export const absoluteUrl = (origin, path = "/") => {
  if (!path) return origin;
  if (/^https?:\/\//i.test(path)) return path;
  return `${String(origin).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

/**
 * Hosts whose images may be run through Netlify Image CDN.
 * MUST stay in sync with `[images] remote_images` in netlify.toml — if a host is
 * listed here but not there, the CDN refuses the transform and the share image
 * 404s, which is worse than shipping the original.
 */
export const SHARE_IMAGE_CDN_HOSTS = ["storage.googleapis.com"];

export const SHARE_IMAGE_WIDTH = 1200;
export const SHARE_IMAGE_HEIGHT = 630;

/**
 * Build the og:image URL for an arbitrary product image.
 *
 * Product photos come straight out of GCS at full resolution — often several MB.
 * WhatsApp silently drops OG images over ~300KB (and rejects non-JPEG/PNG), so a
 * raw bucket URL frequently means "no preview image at all". Routing through
 * Netlify Image CDN gives a cropped, compressed 1200x630 JPEG instead.
 *
 * Falls back to the untouched source URL for any host we have not allow-listed,
 * so an unexpected image host degrades to "big image" rather than "broken image".
 */
export const buildShareImageUrl = (origin, src) => {
  if (!src) return absoluteUrl(origin, DEFAULT_OG_IMAGE);
  if (!/^https?:\/\//i.test(src)) return absoluteUrl(origin, src);

  let host = "";
  try {
    host = new URL(src).hostname;
  } catch {
    return src;
  }

  if (!SHARE_IMAGE_CDN_HOSTS.includes(host)) return src;

  const params = new URLSearchParams({
    url: src,
    w: String(SHARE_IMAGE_WIDTH),
    h: String(SHARE_IMAGE_HEIGHT),
    fit: "cover",
    fm: "jpg",
    q: "80",
  });
  return `${String(origin).replace(/\/+$/, "")}/.netlify/images?${params.toString()}`;
};

/** Resolve metadata for a static path. Returns null for dynamic/unknown routes. */
export const getStaticMeta = (pathname) => {
  const clean = pathname.replace(/\/+$/, "") || "/";
  return ROUTE_META[clean] || null;
};

/**
 * Turn a product payload from GET /public/product/:id/detail into page metadata.
 * Mirrors the copy style of utils/pageHelpers.generateLuxuryWatchContent.
 */
export const buildProductMeta = (product) => {
  if (!product || !product.name) return null;

  const brand = product?.brand?.name || "";
  const model = product?.model?.name || "";
  const detail = product?.product_watchdetail || {};
  const condition = product?.product_condition || {};

  const titleParts = [brand, model, product.name].filter(Boolean);
  const title = `${titleParts.join(" ")} — ${SITE_NAME}`;

  const facts = [
    detail?.year,
    condition?.category,
    detail?.case_diameter?.name,
    detail?.case?.name,
    detail?.dial?.name ? `${detail.dial.name} dial` : null,
    detail?.movement?.name,
  ].filter(Boolean);

  const price =
    product?.is_show_price && product?.selling_price
      ? `Rp ${Number(product.selling_price).toLocaleString("id-ID")}`
      : "Ask for price";

  const description = truncate(
    `${titleParts.join(" ")}${facts.length ? `. ${facts.join(" · ")}` : ""}. ${price}. Authenticated and available at ${SITE_NAME}, Plaza Blok M Jakarta.`,
  );

  const image = product?.product_images?.[0]?.file_url || null;

  return {
    title,
    description,
    image,
    imageAlt: titleParts.join(" "),
    type: "product",
    product: {
      brand,
      sku: product?.sku,
      price: product?.is_show_price ? product?.selling_price : null,
      condition: condition?.category,
      image,
    },
  };
};
